import React, {useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mr, mt, onDesktop, p, w100} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {gtmEventCreditCalculator} from "../../../tracking/google_tag_manager/gtm_event_credit_calculator";
import {applicantsOptions} from "../../constants/applicants_options";
import {OfferTypeOptions} from "../../constants/OfferTypeOptions";
import {RatingCalculatorRangesOptions} from "../../constants/RatingCalculatorRangesOptions";
import {useCalculatorRatingState} from "../../hooks/use_calculator_state";
import {ModalEventSource} from "../../tracking/analytics/credit_modal_event";
import {CreditRatingCalculatorEventAction} from "../../tracking/credit_gtm";
import {getCreditEventAction} from "../../tracking/get_credit_event_action";
import {CreditAlgolyticsViewType} from "../../tracking/types/CreditAlgolyticsViewType";
import {AllCreditGtmActionTypes} from "../../tracking/types/CreditGtmActionTypes";
import {useManageGtmEvent} from "../../tracking/use_manage_gtm_input_events";
import {CalculatorValueType} from "../../types/CalculatorValueType";
import {ICreditRatingData} from "../../types/ICreditData";
import {getCalculatorInitialValues} from "../../utils/get_calculator_initial_values";
import {CreditApplicationModal} from "../CreditApplicationModal";
import {RangeWithInput} from "../RangeWithInput";
import {ExpertAdvice} from "./components/ExpertAdvice";

export const CreditRatingCalculator = () => {
    const {averageEarnings, householdExpenses, loanInstallments, age, updateValue} = useCalculatorRatingState(
        getCalculatorInitialValues(RatingCalculatorRangesOptions)
    );
    const [offerType, setOfferType] = useState<IOption>(OfferTypeOptions[0]);
    const [applicants, setApplicants] = useState<IOption>({label: "2", value: 2});

    const [isOpen, setIsOpen] = useState(false);

    const calculatorSource = CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING;

    const {eventFromSource, sendEvents, sendCreditEvent} = useManageGtmEvent(calculatorSource);

    const submitData = useMemo(
        (): ICreditRatingData => ({
            source: calculatorSource,
            age: age,
            average_earnings: averageEarnings,
            monthly_household_spendings: householdExpenses,
            other_loans_monthly_payments: loanInstallments,
            applicants_count: applicants.value as number,
            property_type: offerType.value as number
        }),
        [averageEarnings, householdExpenses, loanInstallments, age, applicants, offerType]
    );

    const onClick = () => {
        gtmEventCreditCalculator("next_step");
        setIsOpen(true);
    };

    const onClose = () => {
        setIsOpen(false);
    };

    const getValueFromType = (type: CalculatorValueType) => {
        switch (type) {
            case CalculatorValueType.LOAN_INSTALLMENTS:
                return loanInstallments;
            case CalculatorValueType.HOUSEHOLD_EXPENSES:
                return householdExpenses;
            case CalculatorValueType.AVERAGE_EARNINGS:
                return averageEarnings;
            case CalculatorValueType.AGE:
            default:
                return age;
        }
    };

    const onValueChange = (type: CalculatorValueType, value: number, eventActionType: AllCreditGtmActionTypes) => {
        const action = getCreditEventAction(calculatorSource, eventActionType);

        if (action && !sendEvents.includes(action)) {
            sendCreditEvent(action);
        }

        updateValue(type, value);
    };

    const onSelectChange = (option: IOption, action: CreditRatingCalculatorEventAction) => {
        if (action && !sendEvents.includes(action)) {
            sendCreditEvent(action);
        }
        if (action === CreditRatingCalculatorEventAction.APPLICANTS) {
            setApplicants(option);
            return;
        }
        setOfferType(option);
    };

    return (
        <div css={calculatorWrapper}>
            <div css={selectBox} id={CREDIT_PAGES_IDS.CREDITWORTHINESS_SUBPAGE.CREDITWORTHINESS_DROPDOWNS}>
                <Select
                    labelContent="Rodzaj nieruchomości"
                    value={offerType}
                    options={OfferTypeOptions}
                    onChange={(option) => {
                        onSelectChange(option as IOption, CreditRatingCalculatorEventAction.PROPERTY_TYPE);
                    }}
                    css={selectStyle}
                />
                <Select
                    labelContent="Liczba wnioskodawców"
                    value={applicants}
                    options={applicantsOptions}
                    onChange={(option) => {
                        onSelectChange(option as IOption, CreditRatingCalculatorEventAction.APPLICANTS);
                    }}
                    css={selectStyle}
                />
            </div>
            <div css={formWrapper}>
                <div css={onDesktop(mr(3))}>
                    {RatingCalculatorRangesOptions.map((el) => (
                        <div key={el.name} css={mt(3)}>
                            <RangeWithInput
                                rangeOptions={el}
                                value={getValueFromType(el.valueType)}
                                hideLabels
                                updateValue={(type, value) => onValueChange(type, value, el.name)}
                            />
                        </div>
                    ))}
                </div>

                <ExpertAdvice onButtonClick={onClick} />
            </div>
            <CreditApplicationModal
                isOpen={isOpen}
                onClose={onClose}
                submitData={submitData}
                gtmEvent={eventFromSource}
                algolyticData={{type: CreditAlgolyticsViewType.RATING, source: ModalEventSource.CREDIT_RATING}}
            />
        </div>
    );
};

const selectStyle = (theme: Theme) => css`
    width: 100%;
    @media (min-width: ${theme.breakpoints.lg}) {
        width: 50%;
    }
`;

const calculatorWrapper = css`
    ${elevation()};
    ${borderRadius(2)};
    ${p(4)};
    background-color: #fff;
    ${w100};

    margin: 8rem auto 0 auto;
    max-width: 744px;
`;

const formWrapper = css`
    flex-direction: column;
`;

const selectBox = css`
    ${flexAbsoluteCenter};
    ${w100};
    flex-direction: column;
    gap: ${calculateRemSize(4)};

    ${onDesktop(css`
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    `)};
`;
import React, {useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mr, mt, onDesktop, p, w100} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {gtmEventCreditCalculator} from "../../../tracking/google_tag_manager/gtm_event_credit_calculator";
import {applicantsOptions} from "../../constants/applicants_options";
import {OfferTypeOptions} from "../../constants/OfferTypeOptions";
import {RatingCalculatorRangesOptions} from "../../constants/RatingCalculatorRangesOptions";
import {useCalculatorRatingState} from "../../hooks/use_calculator_state";
import {ModalEventSource} from "../../tracking/analytics/credit_modal_event";
import {CreditRatingCalculatorEventAction} from "../../tracking/credit_gtm";
import {getCreditEventAction} from "../../tracking/get_credit_event_action";
import {CreditAlgolyticsViewType} from "../../tracking/types/CreditAlgolyticsViewType";
import {AllCreditGtmActionTypes} from "../../tracking/types/CreditGtmActionTypes";
import {useManageGtmEvent} from "../../tracking/use_manage_gtm_input_events";
import {CalculatorValueType} from "../../types/CalculatorValueType";
import {ICreditRatingData} from "../../types/ICreditData";
import {getCalculatorInitialValues} from "../../utils/get_calculator_initial_values";
import {CreditApplicationModal} from "../CreditApplicationModal";
import {RangeWithInput} from "../RangeWithInput";
import {ExpertAdvice} from "./components/ExpertAdvice";

export const CreditRatingCalculator = () => {
    const {averageEarnings, householdExpenses, loanInstallments, age, updateValue} = useCalculatorRatingState(
        getCalculatorInitialValues(RatingCalculatorRangesOptions)
    );
    const [offerType, setOfferType] = useState<IOption>(OfferTypeOptions[0]);
    const [applicants, setApplicants] = useState<IOption>({label: "2", value: 2});

    const [isOpen, setIsOpen] = useState(false);

    const calculatorSource = CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING;

    const {eventFromSource, sendEvents, sendCreditEvent} = useManageGtmEvent(calculatorSource);

    const submitData = useMemo(
        (): ICreditRatingData => ({
            source: calculatorSource,
            age: age,
            average_earnings: averageEarnings,
            monthly_household_spendings: householdExpenses,
            other_loans_monthly_payments: loanInstallments,
            applicants_count: applicants.value as number,
            property_type: offerType.value as number
        }),
        [averageEarnings, householdExpenses, loanInstallments, age, applicants, offerType]
    );

    const onClick = () => {
        gtmEventCreditCalculator("next_step");
        setIsOpen(true);
    };

    const onClose = () => {
        setIsOpen(false);
    };

    const getValueFromType = (type: CalculatorValueType) => {
        switch (type) {
            case CalculatorValueType.LOAN_INSTALLMENTS:
                return loanInstallments;
            case CalculatorValueType.HOUSEHOLD_EXPENSES:
                return householdExpenses;
            case CalculatorValueType.AVERAGE_EARNINGS:
                return averageEarnings;
            case CalculatorValueType.AGE:
            default:
                return age;
        }
    };

    const onValueChange = (type: CalculatorValueType, value: number, eventActionType: AllCreditGtmActionTypes) => {
        const action = getCreditEventAction(calculatorSource, eventActionType);

        if (action && !sendEvents.includes(action)) {
            sendCreditEvent(action);
        }

        updateValue(type, value);
    };

    const onSelectChange = (option: IOption, action: CreditRatingCalculatorEventAction) => {
        if (action && !sendEvents.includes(action)) {
            sendCreditEvent(action);
        }
        if (action === CreditRatingCalculatorEventAction.APPLICANTS) {
            setApplicants(option);
            return;
        }
        setOfferType(option);
    };

    return (
        <div css={calculatorWrapper}>
            <div css={selectBox} id={CREDIT_PAGES_IDS.CREDITWORTHINESS_SUBPAGE.CREDITWORTHINESS_DROPDOWNS}>
                <Select
                    labelContent="Rodzaj nieruchomości"
                    value={offerType}
                    options={OfferTypeOptions}
                    onChange={(option) => {
                        onSelectChange(option as IOption, CreditRatingCalculatorEventAction.PROPERTY_TYPE);
                    }}
                    css={selectStyle}
                />
                <Select
                    labelContent="Liczba wnioskodawców"
                    value={applicants}
                    options={applicantsOptions}
                    onChange={(option) => {
                        onSelectChange(option as IOption, CreditRatingCalculatorEventAction.APPLICANTS);
                    }}
                    css={selectStyle}
                />
            </div>
            <div css={formWrapper}>
                <div css={onDesktop(mr(3))}>
                    {RatingCalculatorRangesOptions.map((el) => (
                        <div key={el.name} css={mt(3)}>
                            <RangeWithInput
                                rangeOptions={el}
                                value={getValueFromType(el.valueType)}
                                hideLabels
                                updateValue={(type, value) => onValueChange(type, value, el.name)}
                            />
                        </div>
                    ))}
                </div>

                <ExpertAdvice onButtonClick={onClick} />
            </div>
            <CreditApplicationModal
                isOpen={isOpen}
                onClose={onClose}
                submitData={submitData}
                gtmEvent={eventFromSource}
                algolyticData={{type: CreditAlgolyticsViewType.RATING, source: ModalEventSource.CREDIT_RATING}}
            />
        </div>
    );
};

const selectStyle = (theme: Theme) => css`
    width: 100%;
    @media (min-width: ${theme.breakpoints.lg}) {
        width: 50%;
    }
`;

const calculatorWrapper = css`
    ${elevation()};
    ${borderRadius(2)};
    ${p(4)};
    background-color: #fff;
    ${w100};

    margin: 8rem auto 0 auto;
    max-width: 744px;
`;

const formWrapper = css`
    flex-direction: column;
`;

const selectBox = css`
    ${flexAbsoluteCenter};
    ${w100};
    flex-direction: column;
    gap: ${calculateRemSize(4)};

    ${onDesktop(css`
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    `)};
`;
